import * as StandardEventsLibrary from '@shopify/events';
import { isDesignMode, guarded } from '@theme/standard-events-utils';

const CartLinesUpdateEvent = StandardEventsLibrary.CartLinesUpdateEvent;
const CartNoteUpdateEvent = StandardEventsLibrary.CartNoteUpdateEvent;
const CartDiscountUpdateEvent = StandardEventsLibrary.CartDiscountUpdateEvent;
const CartErrorEvent = StandardEventsLibrary.CartErrorEvent;

if (!isDesignMode()) {
  bridge('theme:cart:lines-update', CartLinesUpdateEvent, ['action', 'context', 'lines']);
  bridge('theme:cart:note-update', CartNoteUpdateEvent, ['context', 'note']);
  bridge('theme:cart:discount-update', CartDiscountUpdateEvent, ['context', 'discountCodes']);
  bridgeErrors();
  bridgeLegacyAdditions();
}

function isFullCart(data) {
  return Boolean(data && Array.isArray(data.items) && data.item_count !== undefined);
}

function fetchCart() {
  const base = (window.routes && window.routes.cart_url) || '/cart';

  return fetch(`${base}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

function toStandardCart(EventClass, cart) {
  if (!isFullCart(cart)) return null;

  const converter =
    (EventClass && typeof EventClass.createCartFromAjaxResponse === 'function' && EventClass) ||
    (CartLinesUpdateEvent && typeof CartLinesUpdateEvent.createCartFromAjaxResponse === 'function' && CartLinesUpdateEvent);
  if (!converter) return null;

  try {
    return converter.createCartFromAjaxResponse(cart);
  } catch (error) {
    return null;
  }
}

function toStandardResult(EventClass, result) {
  const source = result || {};
  const cartPromise = isFullCart(source.cart) ? Promise.resolve(source.cart) : fetchCart();

  return cartPromise.then((cart) => {
    const standard = {
      cart: toStandardCart(EventClass, cart),
      detail: { source: 'breeq-theme', ...(source.detail || {}) },
    };
    if (Array.isArray(source.userErrors) && source.userErrors.length) standard.userErrors = source.userErrors;
    if (Array.isArray(source.warnings) && source.warnings.length) standard.warnings = source.warnings;
    return standard;
  });
}

function targetOf(event) {
  return event.target && typeof event.target.dispatchEvent === 'function' ? event.target : document;
}

function bridge(themeEventName, EventClass, keys) {
  if (typeof EventClass !== 'function') return;

  document.addEventListener(
    themeEventName,
    guarded((event) => {
      const detail = event.detail;
      if (!detail || !detail.promise || typeof detail.promise.then !== 'function') return;

      const payload = {};
      keys.forEach((key) => {
        payload[key] = detail[key];
      });
      payload.promise = detail.promise.then((result) => toStandardResult(EventClass, result));
      payload.promise.catch(() => {});
      if (detail.detail) payload.detail = detail.detail;

      targetOf(event).dispatchEvent(new EventClass(payload));
    })
  );
}

function bridgeErrors() {
  if (typeof CartErrorEvent !== 'function') return;

  document.addEventListener(
    'theme:cart:error',
    guarded((event) => {
      const detail = event.detail || {};

      targetOf(event).dispatchEvent(
        new CartErrorEvent({
          error: String(detail.error || detail.message || 'Cart request failed'),
          code: detail.code || 'SERVICE_UNAVAILABLE',
        })
      );
    })
  );
}

function bridgeLegacyAdditions() {
  if (typeof CartLinesUpdateEvent !== 'function' || typeof CartLinesUpdateEvent.createPromise !== 'function') return;

  document.addEventListener(
    'theme:cart-lines-added',
    guarded((event) => {
      const response = event.detail && event.detail.response;
      const lines = response && Array.isArray(response.items) ? response.items : [];
      if (!lines.length) return;

      const deferred = CartLinesUpdateEvent.createPromise();

      targetOf(event).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({
            merchandiseId: String(line.variant_id || line.id || ''),
            quantity: Number(line.quantity) || 1,
          })),
          promise: deferred.promise,
        })
      );

      toStandardResult(CartLinesUpdateEvent, { cart: null }).then(deferred.resolve, deferred.reject);
    })
  );
}
